import type {
  MCPServerListItem,
  MCPServerUserVersion,
  MCPVersionEntry,
} from '@kirin/api-client/contracts/mcp';
import { compareMcpVersionEntries } from '@kirin/api-client/helpers/mcp-version';

/**
 * What the Version column renders for one row (KIR-4844).
 *
 * It headlines only the `installed` entries of the merged `versions` list —
 * read off the artifact on the endpoint. A self-declared (handshake) entry is
 * never the cell's value (KIR-5222): it is attacker-controlled and routinely
 * not the server's version at all (`server-memory` answers `0.6.3` while the
 * install is `2026.8.31`; a Rust server answers with its MCP SDK's version; a
 * remote one with a hard-coded `1.0.0`). A row with nothing installed shows an
 * em dash and the reason, and the handshake stays in the details popover,
 * labelled as the claim it is.
 *
 * The decision lives here rather than in the component: which machine to
 * believe, which "no version" reason to surface, and when a version is not safe
 * to show at all.
 */

/**
 * Why no version was resolved, in the order the column prefers to report it.
 *
 * Order is precedence. A row is a GROUP and machines disagree, so the most
 * definitive reason wins: `not-applicable` is permanent, `not-installed` is one
 * machine at one moment, the rest are degrees of "we could not look".
 *
 * The two PyPI reasons sit above `not-installed` for that same reason.
 * `ambiguous-environments` is the only entry here backed by a live
 * OBSERVATION — copies were seen running, at versions that disagree — so it
 * outranks every explanation of why nothing was found. `foreign-index` is a
 * property of the CONFIGURATION, true on every machine that shares it, so it
 * outranks one machine's momentary miss. Listed in REASON_COPY but omitted
 * here, both were unreachable in a group that also carried `not-installed`:
 * the row headlined "Never started" for a server demonstrably running.
 *
 * The two OCI reasons sit beside their closest npm/PyPI equivalent rather than
 * above it. `not-pulled` is the same shape as `not-installed` — one machine at
 * one moment, undone by the next run — and `locally-built-no-repodigest` is the
 * same shape as `local-project`: a permanent property of what was configured,
 * but one that describes only the machine that built it. `daemon-unreachable`
 * is deliberately absent: it says nothing about the server, only that we could
 * not look, so it must never outrank a reason that does. `transport-unsupported`
 * and `daemon-endpoint-refused` are left out for that same reason — each is a
 * statement about Kirin, not about the server.
 */
const REASON_PRECEDENCE = [
  'not-applicable',
  'ambiguous-environments',
  'foreign-index',
  'not-installed',
  'not-pulled',
  'local-project',
  'locally-built-no-repodigest',
  'git-ref',
] as const;

export interface ReasonCopy {
  /** Short label rendered in the cell. */
  label: string;
  /** Tooltip body — says what the state means, never that something failed. */
  description: string;
}

/**
 * The closed part of the `resolution_method` vocabulary, as the agent defines it
 * in `extensions/agent/pkg/mcp/identity`.
 *
 * Not a TypeScript union: the method is a free string on the wire, since the
 * fleet runs agents newer than this frontend. Unknown values fall through to
 * {@link UNRESOLVED_FALLBACK} rather than blanking the cell.
 */
const REASON_COPY: Record<string, ReasonCopy> = {
  'not-applicable': {
    label: 'Runs remotely',
    description:
      "This server runs on someone else's machine, so there is no installed copy here to read a version from. There never will be.",
  },
  'not-installed': {
    label: 'Never started',
    description:
      'Set up, but never run here, so nothing has been downloaded yet. A version will appear the first time it runs.',
  },
  'local-project': {
    label: 'Someone’s own code',
    description:
      "This is a user's own folder or script rather than a published package, so there is no release to look the version up in.",
  },
  'git-ref': {
    label: 'Installed from Git',
    description:
      'Installed straight from a Git branch or commit, which may never have been released under any version number.',
  },
  'no-resolver': {
    label: 'Not supported yet',
    description:
      'Kirin recognises how this server is packaged but cannot read its version yet. This does not mean it is missing — nothing has looked.',
  },
  'unsupported-runner': {
    label: 'Unknown packaging',
    description:
      'Kirin does not recognise the command this server starts with, so it cannot tell where the version would be.',
  },
  'unsupported-subcommand': {
    label: 'Unknown packaging',
    description:
      'Kirin recognises the command but not the way it is being used here, so it cannot tell where the version would be.',
  },
  'spec-unreadable': {
    label: 'Unknown packaging',
    description:
      'The package name in this server’s configuration could not be read, so there is nothing to look up.',
  },
  'npx-cache-miss': {
    label: 'Couldn’t read it',
    description:
      'Kirin looked where this package would have been downloaded and found nothing matching it.',
  },
  // KIR-5222: the same miss for pnpm's and Bun's run-once caches.
  'dlx-cache-miss': {
    label: 'Couldn’t read it',
    description:
      'Kirin looked where this package would have been downloaded and found nothing matching it.',
  },
  'npx-cache-unsettled': {
    label: 'Couldn’t read it',
    description:
      'A download was still in progress while Kirin looked, so any answer would have been unreliable. The next check tries again.',
  },
  'scan-cancelled': {
    label: 'Check interrupted',
    description:
      'The check ended before it got to this server. The next one tries again.',
  },
  'not-pulled': {
    label: 'Image not downloaded',
    description:
      'Docker has not downloaded this image yet, so there is nothing here to read a version from. That is not the same as unused: a large image can take over a minute on first run, and a client that gives up waiting leaves the machine looking exactly like one where the server was never started.',
  },
  'daemon-unreachable': {
    label: 'Docker not running',
    description:
      'Docker was not running, or Kirin could not reach it, so no image could be inspected. This describes right now rather than how the server is set up, and the next check tries again.',
  },
  'transport-unsupported': {
    label: 'Docker not supported here',
    description:
      'Kirin found Docker on this machine but cannot connect the way it is set up to listen. Unlike Docker simply not running, waiting does not fix this — it needs a newer Kirin, or Docker configured differently.',
  },
  'daemon-endpoint-refused': {
    label: 'Docker not inspected',
    description:
      'This server’s own configuration pointed Kirin at Docker on another machine, and Kirin did not contact it. A configuration file travels with the repository it lives in, so an address written there is a claim rather than a fact about this machine. Docker that the machine itself is set up to use is inspected normally.',
  },
  'locally-built-no-repodigest': {
    label: 'Built locally',
    description:
      'This image was built on the machine rather than downloaded, so nobody outside has vouched for what is in it and there is no released version to match. It may still label itself as a well-known server; that is a claim, not evidence.',
  },
  'toolkit-gateway': {
    label: 'Stands for several servers',
    description:
      'This entry is the Docker MCP Toolkit gateway. It is not a server itself — it stands for several at once, and each of those appears here as its own row with its own version.',
  },
  'toolkit-entry-not-pinned': {
    label: 'Changes on every start',
    description:
      'The Docker MCP Toolkit lists this server loosely rather than by an exact image, so what actually runs is chosen at start-up and cannot be known in advance.',
  },
  'toolkit-profile-not-found': {
    label: 'Couldn’t read it',
    description:
      'The gateway is set up to use a Docker MCP Toolkit profile that is not on this machine, so nothing says which image this server runs.',
  },
  'toolkit-not-configured': {
    label: 'Couldn’t read it',
    description:
      'The Docker MCP Toolkit gateway is set up, but Kirin found nothing on this machine saying which servers it runs.',
  },
  'not-in-any-cached-catalog': {
    label: 'Not in the catalog',
    description:
      'This server is switched on in the Docker MCP Toolkit but is in none of the catalogues on this machine, so nothing records which image it would run. The gateway could not start it either.',
  },
  'foreign-index': {
    label: 'Private registry',
    description:
      'This installs from a private package registry. A name there can mean something different from the same name in public, so Kirin will not match it against a public release.',
  },
  'ambiguous-environments': {
    label: 'Conflicting versions',
    description:
      'Two copies are running at different versions on one machine, usually because different tools picked up different installs. Kirin shows no version rather than picking one of them for you.',
  },
};

/**
 * Plain-language "how we know" for the methods that SUCCEEDED (KIR-5042).
 *
 * REASON_COPY covers only the states where there is no version; these are the
 * twelve where there IS one, and before this slice they had no copy at all —
 * `resolutionMethodLabel` returned the raw value, so the UI printed
 * `npx-cache`, `repo-digest` and `uvx-cache-newest` at a reader who has no
 * reason to know what any of those are.
 *
 * Each phrase answers "how do you know this version?" in one clause, and keeps
 * the distinction that matters: whether the evidence is the code that is
 * actually RUNNING, a copy sitting on disk, or an image a registry vouched for.
 * Those are different strengths of claim, and flattening them to "found it"
 * would throw away the only thing this field is for.
 *
 * Open vocabulary, deliberately: the agent ships independently and is often
 * newer than this frontend, so an unknown value falls through to the raw string
 * rather than blanking the row.
 */
const METHOD_COPY: Record<string, string> = {
  'npx-cache': 'From the downloaded copy',
  // KIR-5222: read off the program the configured command actually launches.
  'npm-global': 'From the globally installed package',
  'npm-project': 'From the project’s installed package',
  'path-venv': 'From the environment the command lives in',
  'homebrew-cellar': 'From the Homebrew install',
  'app-bundle': 'From the app it ships inside',
  'install-dir-version': 'From the folder it is installed in',
  'running-process': 'From the copy that is running',
  'pnpm-dlx-cache': 'From the newest downloaded copy',
  'bunx-cache': 'From the copy downloaded for this server',
  'pypi-running-process': 'From the copy that is running',
  'config-venv-path': 'From the environment it is set up to use',
  'uv-tool-dir': 'From the installed tool',
  'pipx-venv': 'From the installed tool',
  'uvx-cache-newest': 'From the newest downloaded copy',
  'repo-digest': 'From the downloaded image',
  'repo-digest-unverified-store': 'From the local image record',
  'repo-digest-foreign-runtime': 'From the downloaded image',
  'repo-digest-untrusted-endpoint': 'From an image source it chose itself',
  'toolkit-profile-db': 'From the Docker MCP Toolkit',
  'toolkit-catalog': 'From the Docker MCP Toolkit catalogue',
  // KIR-5345: a Claude Desktop extension's bundle, and a code-editor extension.
  'mcpb-manifest': 'From the Claude Desktop extension it was installed from',
  'ide-extension': 'From the code-editor extension it ships inside',
};

/** Used for any `resolution_method` this frontend has not been taught yet. */
const UNRESOLVED_FALLBACK: ReasonCopy = {
  label: 'Couldn’t read it',
  description: 'Kirin could not read a version from the installed copy.',
};

/**
 * A floating version is only meaningful beside the moment it was read, so a row
 * missing that moment gets no number rather than a stale-looking one.
 */
const FLOATING_WITHOUT_TIMESTAMP: ReasonCopy = {
  label: 'Can’t be pinned down',
  description:
    'This server picks a new version every time it starts, and the version Kirin saw came without a time, so there is no saying when it was true.',
};

/**
 * One USER in the drilldown has no version (KIR-5042).
 *
 * Deliberately vaguer than the row-level reasons: the popup knows only this
 * user's rows, not the group's `resolutionMethods`, so it cannot say WHICH of
 * "runs remotely", "never spawned" or "could not be read" applies. Claiming one
 * would be a guess; the row's own cell is where the reason lives.
 */
export const NO_USER_VERSION: ReasonCopy = {
  label: 'No version',
  description:
    "Kirin has not read a version from this person's machines. The server's own Version column says why.",
};

/**
 * Only the server's own handshake claim is known, and no endpoint has reported
 * how it looked for an installed copy (an agent older than identity reporting).
 */
const SELF_DECLARED_ONLY: ReasonCopy = {
  label: 'Not verified',
  description:
    'Only the version this server reports about itself is known, and that is often not its real version. The details show it.',
};

/** No endpoint has reported identity for this row at all. */
const UNREPORTED: ReasonCopy = {
  label: 'Not checked yet',
  description: 'No Kirin agent has reported a version for this server yet.',
};

export type McpVersionState =
  /**
   * At least one version is showable.
   *
   * Only the two lists the CELL reads. The details panel is not served from
   * here — it derives its own installed/handshake lists straight off
   * `row.versions` through `installedVersions` / `selfDeclaredVersions`, so a
   * third "every showable entry" list on this type had no reader and was
   * dropped (KIR-5042). `rankedVersions` is the exported way to get it.
   *
   * `userEntries` is the showable set reduced to ONE PER USER and then DEDUPED
   * across users, and it is what the cell's `+N` counts. It is a count of
   * distinct versions people are on, not a head-count: five users all on two
   * versions give two entries, so the cell reads `+1`. `row.versions` counts
   * evidence instead — a user with `2.0.0` installed whose handshake also
   * claims `1.0.0` contributes two entries while being one person on one
   * version. `userEntries[0]` is the headline, so the two agree on it.
   */
  | {
      kind: 'versions';
      /**
       * The version the cell prints. Its own field rather than
       * `userEntries[0]`: this branch exists only when there IS one, and saying
       * so in the type is what lets the component read it without a non-null
       * assertion.
       */
      headline: MCPVersionEntry;
      userEntries: MCPVersionEntry[];
    }
  /**
   * Nothing showable. The cell prints an em dash and carries the reason in its
   * tooltip — one quiet shape for every "no version" case, rather than a badge
   * per reason.
   */
  | { kind: 'none'; label: string; description: string };

type IdentityFields = Pick<
  MCPServerListItem,
  'versions' | 'resolutionMethods' | 'userVersions'
>;

/**
 * The entries this column is willing to put on screen ANYWHERE — cell, `+N`
 * list or details panel.
 *
 * A floating version is a point-in-time fact, so one that arrived without its
 * point in time is dropped rather than printed as though it still held.
 * Absent `floating` is NOT false — only a positive `true` demands a timestamp.
 *
 * Every surface filters through here. The cell and the panel disagreeing — the
 * panel reprinting a number the cell just suppressed — is the bug this single
 * definition exists to prevent.
 */
export function showableVersions(
  versions: MCPVersionEntry[] | undefined
): MCPVersionEntry[] {
  return (versions ?? []).filter(
    entry => entry.floating !== true || entry.resolvedAt != null
  );
}

/**
 * Showable entries, ordered best-first: installed ahead of self-declared, then
 * by version descending — compared NATURALLY, so `0.10.0` ranks above `0.9.2`.
 *
 * Shares `compareMcpVersionEntries` with the backend's SQL pick and with the
 * users drilldown. Re-ranking here rather than trusting the arrival order is
 * deliberate: the list arrives ordered, but a cell that silently depends on
 * that is a cell that breaks the next time a projection changes, and the
 * comparator costs nothing on a list this size.
 */
export function rankedVersions(
  versions: MCPVersionEntry[] | undefined
): MCPVersionEntry[] {
  return [...showableVersions(versions)].sort(compareMcpVersionEntries);
}

/** Identity of one entry for set membership: a version AND where it came from. */
function entryKey(entry: MCPVersionEntry): string {
  return `${entry.source}:${entry.version}`;
}

/**
 * The per-user version set the cell's `+N` counts (KIR-5042), best-first.
 *
 * Filtered through `showableVersions` by way of `versions`, not used raw. The
 * per-user rollup carries only a version and its source — it has no `floating`
 * flag to judge — so an entry the cell suppressed as unshowable would otherwise
 * reappear in the count, and the cell would read `—  +1`. Every surface
 * filtering through one gate is exactly what `showableVersions` exists for.
 *
 * Falls back to the full showable list when `userVersions` is absent: an older
 * backend that does not send it, for which "one entry per reported version" is
 * the best available answer.
 */
export function userVersionSet(row: IdentityFields): MCPVersionEntry[] {
  const showable = rankedVersions(row.versions);
  if (row.userVersions == null) return showable;
  const allowed = new Set(showable.map(entryKey));
  return [...row.userVersions]
    .filter(entry => allowed.has(entryKey(entry)))
    .sort(compareMcpVersionEntries);
}

/** The showable installed entries, best-first. */
export function installedVersions(
  versions: MCPVersionEntry[] | undefined
): MCPVersionEntry[] {
  return rankedVersions(versions).filter(entry => entry.source === 'installed');
}

/** The showable self-declared (handshake) entries of a row. */
export function selfDeclaredVersions(
  versions: MCPVersionEntry[] | undefined
): MCPVersionEntry[] {
  return rankedVersions(versions).filter(
    entry => entry.source === 'self-declared'
  );
}

/**
 * What the cell shows: the best showable INSTALLED version, read off an
 * artifact, or an em dash with the reason when there is none. A declared
 * (handshake) value is never the cell's value — not even in an otherwise empty
 * cell (KIR-5222) — and stays in the details popover, labelled as the claim it
 * is.
 */
export function deriveMcpVersionState(row: IdentityFields): McpVersionState {
  const methods = row.resolutionMethods ?? [];

  // Installed only (KIR-5222): the handshake is a claim, never the headline.
  // Already filtered of the unshowable entries, so this list is safe to print.
  const [headline] = installedVersions(row.versions);
  if (headline) {
    return {
      kind: 'versions',
      headline,
      userEntries: userVersionSet(row).filter(
        entry => entry.source === 'installed'
      ),
    };
  }

  // An install existed but was dropped as unshowable, rather than never
  // existing — say that, instead of a reason that would misdescribe it.
  if ((row.versions ?? []).some(entry => entry.source === 'installed')) {
    return { kind: 'none', ...FLOATING_WITHOUT_TIMESTAMP };
  }

  // No reason: nothing has LOOKED, which is distinct from every reason below.
  // Gateway rows land here and must not read not-installed. A handshake alone
  // gets its own copy, so the popover holding it is not a surprise.
  if (methods.length === 0) {
    return {
      kind: 'none',
      ...(selfDeclaredVersions(row.versions).length > 0
        ? SELF_DECLARED_ONLY
        : UNREPORTED),
    };
  }

  for (const method of REASON_PRECEDENCE) {
    if (methods.includes(method)) {
      return { kind: 'none', ...(REASON_COPY[method] ?? UNRESOLVED_FALLBACK) };
    }
  }

  // Keep the specific wording when the method is recognised.
  const known = methods.find(method => method in REASON_COPY);
  const copy = known ? REASON_COPY[known] : UNRESOLVED_FALLBACK;
  return { kind: 'none', ...(copy ?? UNRESOLVED_FALLBACK) };
}

/**
 * Where one version came from — the resolver that read it, or the handshake
 * that asserted it. Shown beside every version so "the old way" is always
 * identifiable rather than inferred.
 */
export function versionOriginLabel(
  // Both shapes, named rather than structural: the drilldown's per-user entry
  // is narrower than a list entry and its `method` is nullable-not-optional, so
  // neither is assignable to the other. A `Pick` would have to carry an index
  // signature to survive excess-property checking on a literal, which would
  // accept anything at all.
  entry: MCPVersionEntry | MCPServerUserVersion
): string {
  if (entry.source === 'self-declared') {
    return 'Reported by the server';
  }
  return 'Found on the machine';
}

/**
 * Human label for one `resolution_method` — the popover's "How we know" row.
 *
 * The method is deliberately NOT repeated in the version tooltips: it reads as
 * jargon in a hover, and the popover has the room to frame it.
 *
 * METHOD_COPY first: a method that produced a version is answering "how do you
 * know", and REASON_COPY's labels are written for the opposite case ("Never
 * started", "Runs remotely"). Falls through to the raw value for a method this
 * frontend has not been taught, which is better than a blank row.
 */
export function resolutionMethodLabel(method: string): string {
  return METHOD_COPY[method] ?? REASON_COPY[method]?.label ?? method;
}

/**
 * Whether self-declared and installed disagree. Informational, never a warning —
 * it is normal for official servers whose handshake literal was never updated.
 */
export function hasSelfDeclaredMismatch(
  versions: MCPVersionEntry[] | undefined
): boolean {
  const installed = installedVersions(versions).map(entry => entry.version);
  if (installed.length === 0) return false;
  const declared = selfDeclaredVersions(versions);
  if (declared.length === 0) return false;
  return declared.some(entry => !installed.includes(entry.version));
}
